import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import { useTheme } from "../context/ThemeContext";
import { getPageBackground, getCardBackground, getMutedText, getPrimaryText } from "../utils/themeUtils";
import { Button, Input } from "../components/ui";
import config from "../api/config";
import { getHeaders } from "../api/apiHelpers";

const ForgotPassword: React.FC = () => {
  const location = useLocation();
  // State for handling form input and status messages
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const { isDarkMode } = useTheme();
  // HELPER
  const pathParts = location.pathname.split("/").filter(Boolean);
  const xTenantId = pathParts[0] || null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Basic email validation
    if (!email) {
      setError("Email is required");
      return;
    }

    if (!/\S+@\S+\.\S+/.test(email)) {
      setError("Please enter a valid email address");
      return;
    }

    setError(""); // Clear any previous error message
    setLoading(true);
    setSuccess(false);

    try {
      const response = await fetch(`${config.BASE_URL}/auth/forgot-password`, {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(true);
      } else {
        // Show generic message for security (don't reveal if email exists)
        setError(data.message || "If an account exists with this email, you will receive a password reset link.");
      }
    } catch (err) {
      // Network error - still show generic message for security
      setError("If an account exists with this email, you will receive a password reset link.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen">
      <Navbar transparent={true} />
      <div className={`${getPageBackground(isDarkMode)} flex-1 flex items-center justify-center py-8 sm:py-16 px-4 sm:px-8`}>
        <div className={`${getCardBackground(isDarkMode)} w-full max-w-sm sm:max-w-md min-h-screen p-6 sm:p-8`}>
          <h2 className={`text-2xl sm:text-3xl font-bold text-center mb-6 sm:mb-8 ${getPrimaryText(isDarkMode)}`}>
            Forgot Your Password?
          </h2>

          {/* Form */}
          <form onSubmit={handleSubmit}>
            <Input
              label="Enter your email address"
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email address"
              autoComplete="email"
              required
            />

            {error && <p className="text-red-500 text-sm sm:text-base mt-4">{error}</p>}
            {success && (
              <p className="text-green-500 text-sm sm:text-base mt-4">
                A password reset link has been sent to your email!
              </p>
            )}

            <div className="mt-4 sm:mt-6">
              <Button
                type="submit"
                variant="primary"
                size="lg"
                fullWidth
                loading={loading}
                disabled={loading}
              >
                {loading ? "Sending..." : "Send Reset Link"}
              </Button>
            </div>
          </form>

          <div className="mt-4 sm:mt-6 text-center">
            <p className={`text-sm sm:text-base ${getMutedText(isDarkMode)}`}>
              Remembered your password?{" "}
              <Link
                to={`/${xTenantId}/login`}
                className="text-[var(--cc-link)] hover:opacity-80 transition-opacity duration-200"
              >
                Go back to Login
              </Link>
            </p>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ForgotPassword;
